import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { DefaultCreditCardDelimiter, formatCreditCard, registerCursorTracker } from 'cleave-zen';
import { toast } from 'sonner';
import LoadingSection from '@/components/LoadingSection';
import { DateInput } from '@/components/ui/DateInput';
import { ManualPaymentService, ManualMintPaymentRequest } from '@/api/manualPaymentService';
import { getBuyPanelsPath } from '../route';

type TCreditCardSchema = z.infer<typeof creditCardSchema>;

const creditCardSchema = z.object({
  number: z.string().transform((val) => val.replace(/\s+/g, '')).refine((val) => /^\d{16}$/.test(val), {
    message: 'Card number must be exactly 16 digits',
  }),
  name: z.string().min(1, { message: 'Please enter your name' }),
  expiry: z.string().regex(/^(0[1-9]|1[0-2])\/\d{2}$/, 'Must be in MM/YY.'),
  cvc: z.string().regex(/^\d{3,4}$/, 'Invalid CVC'),
});

interface ManualMintPaymentFormProps {
  requestUuid: string;
  token?: string;
  beforePayment?: () => Promise<boolean>;
}

export default function ManualMintPaymentForm({ requestUuid, token, beforePayment }: ManualMintPaymentFormProps) {
  const routePrefix = getBuyPanelsPath(window.location.pathname, '');
  const form = useForm<TCreditCardSchema>({
    resolver: zodResolver(creditCardSchema),
    defaultValues: {
      number: '',
      name: '',
      expiry: '',
      cvc: '',
    },
  });

  const creditCardRef = useRef<HTMLInputElement>(null);
  const expiryRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!creditCardRef.current) return;

    const cleanupCC = registerCursorTracker({
      input: creditCardRef.current,
      delimiter: DefaultCreditCardDelimiter,
    });

    const cleanupExpiry = expiryRef.current
      ? registerCursorTracker({
          input: expiryRef.current,
          delimiter: '/',
        })
      : undefined;

    return () => {
      cleanupCC?.();
      cleanupExpiry?.();
    };
  }, []);

  const buildSuccessUrl = () => {
    const params = new URLSearchParams();
    params.set('requestId', requestUuid);
    if (token) {
      params.set('token', token);
    }

    return `${window.location.origin}${routePrefix}/manual-payment/success?${params.toString()}`;
  };

  const onSubmit = async (data: TCreditCardSchema) => {
    if (beforePayment) {
      const canProceed = await beforePayment();
      if (!canProceed) {
        return;
      }
    }

    const payment: ManualMintPaymentRequest = {
      number: data.number,
      holderName: data.name,
      expiryMonth: data.expiry.substring(0, 2),
      expiryYear: data.expiry.substring(3, 5),
      cvc: data.cvc,
    };

    try {
      form.clearErrors();
      form.setValue('number', data.number);
      const response = await ManualPaymentService.submitMintPayment(requestUuid, payment, token ?? undefined);

      if (response.confirmed) {
        window.location.assign(buildSuccessUrl());
        return;
      }

      toast.info('Payment is still processing. Please refresh shortly.');
    } catch (error) {
      console.error('Manual Mint payment failed:', error);
      toast.error('Payment failed. Please try again.');
    }
  };

  return (
    <div className="relative">
      {form.formState.isSubmitting && (
        <div className="absolute -inset-1 z-10 flex items-center justify-center bg-brand-neutral-800">
          <LoadingSection />
        </div>
      )}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-6">
            <FormField
              control={form.control}
              name="number"
              render={({ field }) => {
                const combinedRef = mergeRefs(field.ref, creditCardRef);

                return (
                  <FormItem>
                    <FormLabel>Card Number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="XXXX XXXX XXXX XXXX"
                        {...field}
                        ref={combinedRef}
                        onChange={(e) => {
                          const rawValue = e.target.value;
                          const formatted = formatCreditCard(rawValue);
                          field.onChange(formatted);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cardholder Name</FormLabel>
                  <FormControl>
                    <Input placeholder="" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex w-full justify-between gap-6 items-start">
              <FormField
                control={form.control}
                name="expiry"
                render={({ field }) => {
                  const combinedExpiryRef = mergeRefs(field.ref, expiryRef);
                  return (
                    <FormItem className="w-40">
                      <FormLabel>Expiration Date</FormLabel>
                      <FormControl>
                        <DateInput
                          placeholder="MM/YY"
                          {...field}
                          ref={combinedExpiryRef}
                          onValueChange={(val) => field.onChange(val)}
                          datePattern={['m', 'y']}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
              <FormField
                control={form.control}
                name="cvc"
                render={({ field }) => (
                  <FormItem className="w-20">
                    <FormLabel>CVC</FormLabel>
                    <FormControl>
                      <Input placeholder="XXX" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="w-full">
              <Button disabled={form.formState.isSubmitting || !form.formState.isValid} type="submit" className="w-full">
                {form.formState.isSubmitting ? 'Processing...' : 'Pay Balance'}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}

function mergeRefs<T>(...refs: Array<import('react').Ref<T>>) {
  return (value: T | null) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === 'function') {
        ref(value);
      } else if (typeof ref === 'object') {
        (ref as import('react').MutableRefObject<T | null>).current = value;
      }
    });
  };
}
